// Apex Business Intelligence — Round 8 Data Bridge (additive)
// Sync Management Panel, App Switcher, Combined Dashboard Widget, Moisture Map viewer.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "./intelligence";
import { FieldProIQOutboxPanel } from "./fieldproiq";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";
const auth = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? {Authorization: "Bearer " + t} : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, {...opts, headers: {"Content-Type": "application/json", ...auth(), ...(opts.headers || {})}})
    .then(async r => { const data = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(data.detail || r.statusText), {data, status: r.status}); return data; });

const card = {background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8,padding:18,marginBottom:14};
const labelStyle = {fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:"var(--text3)",marginBottom:4,display:"block"};
const subhead = {fontFamily:"var(--rajdhani)",fontSize:14,fontWeight:700,marginBottom:10,letterSpacing:0.5};

// ────────────────────────────────────────────────────────────────
// App Switcher (global header)
// ────────────────────────────────────────────────────────────────
export function AppSwitcher() {
  const [open, setOpen] = useState(false);
  const go = async (target) => {
    try { const r = await j(`/api/iv/bridge/handoff/token?target=${target}`); window.open(r.url, "_blank"); }
    catch (e) { toast(e.message, "err"); }
  };
  const apps = [
    {key:"arm", label:"Response Management", icon:"🚨"},
    {key:"fieldproiq", label:"FieldProIQ", icon:"📍"},
    {key:"intellivoice", label:"IntelliVoice", icon:"📞"},
  ];
  return (
    <div style={{position:"relative"}}>
      <button data-testid="app-switcher" onClick={() => setOpen(!open)} className="btn btn-ghost" style={{fontSize:11,padding:"6px 10px",display:"flex",alignItems:"center",gap:6}}>
        Apps ▾
      </button>
      {open && (
        <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:6,minWidth:220,boxShadow:"0 8px 18px rgba(0,0,0,0.25)",zIndex:80}}>
          {apps.map(a => (
            <div key={a.key} data-testid={`app-${a.key}`} onClick={() => { setOpen(false); go(a.key); }}
                 style={{padding:"10px 14px",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:8,borderBottom:"1px solid var(--border)"}}
                 onMouseDown={e=>e.preventDefault()}>
              <span>{a.icon}</span> Switch to {a.label}
            </div>
          ))}
          <div style={{padding:"8px 14px",fontSize:10,color:"var(--text3)"}}>Opens in a new tab</div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Combined Dashboard Widget (Intelligence + ARM + FieldProIQ)
// ────────────────────────────────────────────────────────────────
export function CombinedDashboardWidget({ onOpenARM }) {
  const [data, setData] = useState(null);
  useEffect(() => { j("/api/iv/bridge/arm/dashboard-summary").then(setData).catch(() => {}); }, []);
  if (!data) return null;
  const cells = [
    ["active_work_orders", "Active Work Orders"],
    ["techs_in_field", "Techs in Field"],
    ["inspections_today", "Inspections Today"],
    ["estimates_pending", "Estimates Pending"],
    ["photos_today", "Photos Uploaded Today"],
    ["moisture_maps_active", "Moisture Maps Active"],
  ];
  return (
    <div data-testid="combined-dashboard-widget" style={card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>Response Management · Live</div>
        <button className="btn btn-ghost" style={{fontSize:11}} onClick={onOpenARM} data-testid="open-arm">Open in Response Management →</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
        {cells.map(([k,l]) => (
          <div key={k} style={{padding:"10px 12px",background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:6}}>
            <div style={{fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:"var(--text3)"}}>{l}</div>
            <div style={{fontFamily:"var(--rajdhani)",fontSize:26,fontWeight:700,color:"#cc0000"}}>{data[k] ?? "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Sync Management Panel (under Administration → Integrations)
// ────────────────────────────────────────────────────────────────
const SYNC_LABELS = {customer:"Customer", job:"Job & Work Order", staff:"Staff", gps:"GPS Location", document:"Document", schedule:"Schedule", notification:"Notification", moisture:"Moisture (FieldProIQ)", intake:"Intake (ARM)"};

export function SyncManagementPanel() {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    j("/api/iv/bridge/status").then(setStatus).catch(() => {});
    j("/api/iv/bridge/config").then(setCfg).catch(() => {});
    j("/api/iv/bridge/log?limit=50").then(d => setLog(d.log || [])).catch(() => {});
    j("/api/iv/bridge/errors").then(d => setErrors(d.errors || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  const toggle = async (k) => {
    if (!cfg) return;
    const next = {...cfg, toggles: {...cfg.toggles, [k]: !cfg.toggles[k]}};
    setCfg(next);
    try { await j("/api/iv/bridge/config", {method:"PUT", body: JSON.stringify(next)}); toast("Sync setting updated"); }
    catch (e) { toast(e.message, "err"); }
  };
  const syncNow = async () => {
    setBusy(true);
    try { const r = await j("/api/iv/bridge/sync-now", {method:"POST"}); toast(`Synced ${Object.values(r.results).filter(v=>typeof v==="number").reduce((a,b)=>a+b,0)} records`); load(); }
    catch (e) { toast(e.message, "err"); }
    finally { setBusy(false); }
  };
  const resolveErr = async (id) => {
    try { await j(`/api/iv/bridge/errors/${id}/resolve`, {method:"POST"}); toast("Resolved"); load(); }
    catch (e) { toast(e.message, "err"); }
  };
  const exportLog = () => {
    const csv = ["timestamp,sync_type,direction,new_count,status", ...log.map(l => `${l.ts},${l.sync_type},${l.direction},${l.new_count},${l.status}`)].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download = `sync-log-${Date.now()}.csv`; a.click();
    toast("Log exported");
  };

  if (!status || !cfg) return <div style={{padding:18}}>Loading…</div>;
  const isLive = status.connection === "live";
  return (
    <div data-testid="sync-mgmt-panel">
      <div style={card}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div>
            <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700}}>Response Management Sync</div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6,fontSize:12}}>
              <span style={{width:10,height:10,borderRadius:"50%",background:isLive?"#1d4ed8":"#cc0000"}}/>
              <span>{isLive ? "Connected" : "Disconnected"}</span>
              <span style={{color:"var(--text3)"}}>· Last sync: {status.last_sync ? new Date(status.last_sync).toLocaleString() : "Never"}</span>
            </div>
          </div>
          <div style={{display:"flex",gap:6}}>
            <button className="btn btn-ghost" style={{fontSize:11}} onClick={exportLog}>Export Log</button>
            <button className="btn btn-primary" style={{fontSize:11}} onClick={syncNow} disabled={busy} data-testid="sync-now">{busy ? "Syncing…" : "Sync Now"}</button>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8,marginBottom:14}}>
          <Stat label="Records synced today" value={status.records_synced_today}/>
          <Stat label="Pending items" value={status.pending}/>
          <Stat label="Errors" value={status.errors} hot={status.errors>0}/>
          <Stat label="Poll interval" value={`${cfg.poll_seconds}s`}/>
        </div>

        <div style={subhead}>Sync Types</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:8}}>
          {Object.entries(SYNC_LABELS).map(([k, label]) => (
            <label key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:"var(--bg3)",borderRadius:4,fontSize:12,cursor:"pointer"}}>
              <span>{label} <span style={{color:"var(--text3)",fontSize:11}}>· {status.source_counts[k] ?? 0} src</span></span>
              <input type="checkbox" checked={!!cfg.toggles[k]} onChange={() => toggle(k)} data-testid={`sync-toggle-${k}`}/>
            </label>
          ))}
        </div>
      </div>

      {errors.length > 0 && (
        <div style={{...card,borderColor:"#cc0000"}}>
          <div style={{...subhead,color:"#cc0000"}}>Sync Errors ({errors.length})</div>
          <table>
            <thead><tr><th>Time</th><th>Type</th><th>Error</th><th></th></tr></thead>
            <tbody>{errors.map(e => (
              <tr key={e.id}>
                <td style={{fontSize:11,color:"var(--text3)"}}>{new Date(e.ts).toLocaleString()}</td>
                <td><span className="badge badge-amber">{e.sync_type}</span></td>
                <td style={{fontSize:11}}>{e.error}</td>
                <td><button className="btn btn-ghost" style={{fontSize:10}} onClick={() => resolveErr(e.id)}>Resolve</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <FieldProIQOutboxPanel />

      <div style={card}>
        <div style={subhead}>Sync Log</div>
        <table>
          <thead><tr><th>Time</th><th>Type</th><th>Direction</th><th>New</th><th>Status</th></tr></thead>
          <tbody>{log.length === 0 && <tr><td colSpan={5} style={{textAlign:"center",color:"var(--text3)",padding:14}}>No sync events yet.</td></tr>}
            {log.slice(0, 30).map(l => (
              <tr key={l.id}>
                <td style={{fontSize:11,color:"var(--text3)"}}>{new Date(l.ts).toLocaleString()}</td>
                <td><span className="badge badge-cyan">{l.sync_type}</span></td>
                <td style={{fontSize:11}}>{l.direction}</td>
                <td style={{fontFamily:"var(--mono)"}}>{l.new_count}</td>
                <td><span className={`badge ${l.status==="success"?"badge-green":"badge-red"}`}>{l.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({label, value, hot}) {
  return (
    <div style={{padding:"8px 12px",background:"var(--bg3)",borderRadius:6}}>
      <div style={{fontSize:9,letterSpacing:1.2,textTransform:"uppercase",color:"var(--text3)"}}>{label}</div>
      <div style={{fontFamily:"var(--rajdhani)",fontSize:22,fontWeight:700,color:hot?"#cc0000":"var(--text)"}}>{value ?? "—"}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Work Order Detail with Moisture Map (read-only from FieldProIQ)
// ────────────────────────────────────────────────────────────────
const ZONE_COLOR = {Dry:"#1d4ed8","Elevated":"#f59e0b",High:"#ea580c",Critical:"#cc0000",Saturated:"#7f1d1d"};

export function ARMWorkOrdersPage() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);   // selected request_id
  const [detail, setDetail] = useState(null);
  const [moisture, setMoisture] = useState(null);
  const [tracking, setTracking] = useState([]);

  useEffect(() => { j("/api/iv/bridge/arm/work-orders?limit=100").then(d => setList(d.work_orders || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!active) { setDetail(null); setMoisture(null); return; }
    j(`/api/iv/bridge/arm/work-orders/${active}`).then(d => setDetail(d.work_order)).catch(() => setDetail(null));
    j(`/api/iv/bridge/moisture/by-request/${active}`).then(setMoisture).catch(() => setMoisture(null));
    j(`/api/iv/bridge/moisture/tracking/${active}`).then(d => setTracking(d.tracking || [])).catch(() => setTracking([]));
  }, [active]);

  const addEquip = async () => {
    const name = window.prompt("Equipment name (e.g. LGR Dehumidifier)");
    if (!name) return;
    try { await j("/api/iv/bridge/moisture/tracking", {method:"POST", body: JSON.stringify({request_id: active, equipment_name: name, status:"Recommended"})});
      const r = await j(`/api/iv/bridge/moisture/tracking/${active}`); setTracking(r.tracking || []); toast("Equipment recommended"); }
    catch (e) { toast(e.message, "err"); }
  };
  const setStatus = async (entryId, status) => {
    try { await j(`/api/iv/bridge/moisture/tracking/${entryId}/status`, {method:"PUT", body: JSON.stringify({status})});
      const r = await j(`/api/iv/bridge/moisture/tracking/${active}`); setTracking(r.tracking || []); toast(`→ ${status}`); }
    catch (e) { toast(e.message, "err"); }
  };

  return (
    <div data-testid="arm-work-orders-page" style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:14}}>
      <div style={{...card,maxHeight:"calc(100vh - 200px)",overflowY:"auto"}}>
        <div style={subhead}>Work Orders (ARM)</div>
        {list.length === 0 ? <div style={{fontSize:12,color:"var(--text3)"}}>No work orders synced yet.</div>
          : list.map(wo => {
            const id = wo.id || wo.request_id;
            return (
              <div key={id} onClick={() => setActive(id)} style={{padding:"8px 10px",borderRadius:4,marginBottom:6,background:active===id?"rgba(204,0,0,0.06)":"var(--bg3)",border:`1px solid ${active===id?"#cc0000":"var(--border)"}`,cursor:"pointer"}}>
                <div style={{fontFamily:"var(--mono)",fontSize:11,color:"var(--text3)"}}>{id}</div>
                <div style={{fontFamily:"var(--rajdhani)",fontWeight:600,fontSize:13}}>{wo.title || wo.description?.slice(0,40) || "(no description)"}</div>
                <div style={{fontSize:11,marginTop:2}}>
                  <span className="badge badge-cyan">{wo.status || "—"}</span>
                  {wo.priority && <span className="badge badge-amber" style={{marginLeft:4}}>{wo.priority}</span>}
                </div>
              </div>
            );
          })}
      </div>

      <div>
        {!active && <div style={{...card,color:"var(--text3)",fontSize:12,textAlign:"center",padding:40}}>Select a work order from the left to see ARM details + moisture map.</div>}
        {active && detail && (
          <>
            <div style={card}>
              <div style={subhead}>{detail.title || detail.description || "Work Order"}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8,fontSize:12}}>
                {[["status","Status"],["priority","Priority"],["address","Address"],["customer_name","Customer"],["customer_phone","Phone"],["claim_number","Claim #"],["adjuster","Adjuster"],["created_at","Created"]].map(([k,l]) => (
                  <div key={k}><div style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",color:"var(--text3)"}}>{l}</div>
                  <div>{(detail[k] || "—").toString().slice(0,80)}</div></div>
                ))}
              </div>
              <div style={{marginTop:10,fontSize:11,color:"var(--text3)"}}>📌 Read-only view from ARM · <strong>Last updated:</strong> {detail.updated_at ? new Date(detail.updated_at).toLocaleString() : "unknown"}</div>
            </div>

            {/* MOISTURE MAP TAB */}
            <div style={card} data-testid="moisture-map-tab">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700}}>Moisture Map (FieldProIQ · read-only)</div>
                <button className="btn btn-ghost" style={{fontSize:11}} onClick={() => {
                  if (!moisture?.moisture_map) return toast("No moisture map data yet", "err");
                  const m = moisture.moisture_map;
                  const lines = [
                    `APEX BUSINESS INTELLIGENCE — DRYING REPORT`,
                    `Work Order: ${active}`,
                    `Room: ${m.room || "—"}    Cause: ${m.cause || "—"}`,
                    `Initial Description: ${m.initial_description || "—"}`,
                    ``,
                    `READINGS:`,
                    ...(m.readings||[]).map(r => `  ${r.pointLabel}: ${r.pct}% · ${r.material} · ${r.zone} · ${r.timestamp}`),
                    ``,
                    `AI ANALYSIS:`,
                    `  Severity: ${m.ai_analysis?.severity || "—"}`,
                    `  IICRC Class: ${m.ai_analysis?.iiicrcClass || "—"}    Category: ${m.ai_analysis?.iiicrcCategory || "—"}`,
                    `  Mold Risk: ${m.ai_analysis?.moldRisk || "—"}`,
                    `  Drying Days: ${m.ai_analysis?.dryingDays || "—"}`,
                    `  Insurance Notes: ${m.ai_analysis?.insuranceNotes || "—"}`,
                  ];
                  const blob = new Blob([lines.join("\n")], {type:"text/plain"});
                  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `drying-report-${active}.txt`; a.click();
                  toast("Drying report downloaded");
                }} data-testid="drying-report">Drying Report</button>
              </div>

              {!moisture?.moisture_map
                ? <div style={{fontSize:12,color:"var(--text3)"}}>No moisture map recorded for this request in FieldProIQ yet.</div>
                : <>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:8,marginBottom:12}}>
                    <Field label="Room" value={moisture.moisture_map.room}/>
                    <Field label="Cause" value={moisture.moisture_map.cause}/>
                    <Field label="Severity" value={moisture.moisture_map.ai_analysis?.severity} kind="severity"/>
                    <Field label="IICRC Class" value={moisture.moisture_map.ai_analysis?.iiicrcClass}/>
                    <Field label="Mold Risk" value={moisture.moisture_map.ai_analysis?.moldRisk}/>
                    <Field label="Drying Days" value={moisture.moisture_map.ai_analysis?.dryingDays}/>
                  </div>

                  {/* Readings table */}
                  <div style={subhead}>Readings ({(moisture.moisture_map.readings || []).length})</div>
                  {(moisture.moisture_map.readings || []).length === 0
                    ? <div style={{fontSize:11,color:"var(--text3)"}}>No readings logged yet.</div>
                    : <table><thead><tr><th>Point</th><th>Material</th><th>%</th><th>Zone</th><th>Risk</th><th>Recommended Action</th><th>Logged</th></tr></thead>
                        <tbody>{moisture.moisture_map.readings.map(r => (
                          <tr key={r.id}>
                            <td style={{fontFamily:"var(--mono)"}}>{r.pointLabel}</td>
                            <td>{r.material}</td>
                            <td><strong>{r.pct}%</strong></td>
                            <td><span style={{padding:"2px 8px",borderRadius:10,background:ZONE_COLOR[r.zone]||"#888",color:"#fff",fontSize:10}}>{r.zone}</span></td>
                            <td>{r.risk}</td>
                            <td style={{fontSize:11}}>{r.recommendedAction}</td>
                            <td style={{fontSize:11,color:"var(--text3)"}}>{r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}</td>
                          </tr>
                        ))}</tbody>
                      </table>}

                  {/* AI Analysis details */}
                  <div style={{...subhead, marginTop:14}}>AI Analysis</div>
                  <div style={{fontSize:12}}>
                    <p>{moisture.moisture_map.ai_analysis?.summary || ""}</p>
                    {moisture.moisture_map.ai_analysis?.primaryConcerns?.length > 0 && <p><strong>Concerns:</strong> {moisture.moisture_map.ai_analysis.primaryConcerns.join(", ")}</p>}
                    {moisture.moisture_map.ai_analysis?.immediateActions?.length > 0 && <p><strong>Immediate Actions:</strong> {moisture.moisture_map.ai_analysis.immediateActions.join(" · ")}</p>}
                    {moisture.moisture_map.ai_analysis?.equipmentRecommended?.length > 0 && <p><strong>Equipment Recommended:</strong> {moisture.moisture_map.ai_analysis.equipmentRecommended.join(", ")}</p>}
                    {moisture.moisture_map.ai_analysis?.insuranceNotes && <p style={{padding:8,background:"rgba(29,78,216,0.06)",borderLeft:"3px solid #1d4ed8"}}><strong>Insurance Notes:</strong> {moisture.moisture_map.ai_analysis.insuranceNotes}</p>}
                  </div>

                  {/* Floor plan canvas */}
                  {(moisture.moisture_map.walls?.length > 0 || moisture.moisture_map.readings?.length > 0) && (
                    <>
                      <div style={{...subhead, marginTop:14}}>Floor Plan</div>
                      <FloorPlan walls={moisture.moisture_map.walls} readings={moisture.moisture_map.readings}/>
                    </>
                  )}
                </>}
            </div>

            {/* Equipment tracking — Intelligence-write-able */}
            <div style={card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={subhead}>Moisture Equipment Tracking</div>
                <button className="btn btn-primary" style={{fontSize:11}} onClick={addEquip} data-testid="add-equip">+ Add Equipment</button>
              </div>
              {tracking.length === 0 ? <div style={{fontSize:11,color:"var(--text3)"}}>No equipment tracked yet. Recommend equipment from the AI analysis above.</div>
                : <table><thead><tr><th>Equipment</th><th>Status</th><th>Qty</th><th>Recommended By</th><th></th></tr></thead>
                    <tbody>{tracking.map(t => (
                      <tr key={t.id}>
                        <td>{t.equipment_name}</td>
                        <td><span className="badge badge-cyan">{t.status}</span></td>
                        <td>{t.quantity}</td>
                        <td style={{fontSize:11,color:"var(--text3)"}}>{t.created_by}</td>
                        <td>
                          <select onChange={e => setStatus(t.id, e.target.value)} value={t.status} style={{padding:"4px 6px",fontSize:11,background:"var(--bg)",border:"1px solid var(--border)",borderRadius:3}}>
                            {["Recommended","Deployed","Removed","Returned"].map(s => <option key={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>}
            </div>

            {/* Photos */}
            {moisture?.photos?.length > 0 && (
              <div style={card}>
                <div style={subhead}>Moisture Photos ({moisture.photos.length})</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:6}}>
                  {moisture.photos.map(p => (
                    <div key={p.id} style={{padding:6,background:"var(--bg3)",borderRadius:4}}>
                      <div style={{height:90,background:"#000",borderRadius:3,marginBottom:4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#666"}}>{p.filename || "(photo)"}</div>
                      <div style={{fontSize:10,color:"var(--text3)"}}>{p.tag} · {p.timestamp ? new Date(p.timestamp).toLocaleDateString() : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Field({label, value, kind}) {
  const colorMap = {Critical:"#cc0000","High":"#ea580c","Elevated":"#f59e0b","Dry":"#1d4ed8","Saturated":"#7f1d1d"};
  return (
    <div style={{padding:"8px 10px",background:"var(--bg3)",borderRadius:6}}>
      <div style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",color:"var(--text3)"}}>{label}</div>
      <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:600,color:kind==="severity"?colorMap[value]||"var(--text)":"var(--text)"}}>{value || "—"}</div>
    </div>
  );
}

function FloorPlan({walls, readings}) {
  const W = 600, H = 360;
  return (
    <div style={{background:"#fff",border:"1px solid var(--border)",borderRadius:6,padding:10,maxWidth:W+20}}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{display:"block"}}>
        <rect x={0} y={0} width={W} height={H} fill="#f5f5f5"/>
        {(walls || []).map((w, i) => (
          <line key={i} x1={w.x1*W} y1={w.y1*H} x2={w.x2*W} y2={w.y2*H} stroke="#222" strokeWidth={3}/>
        ))}
        {(readings || []).map(r => (
          <g key={r.id}>
            <circle cx={r.x*W} cy={r.y*H} r={12} fill={ZONE_COLOR[r.zone]||"#888"} stroke="#fff" strokeWidth={2}/>
            <text x={r.x*W} y={r.y*H+4} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">{r.pointLabel}</text>
          </g>
        ))}
      </svg>
      <div style={{marginTop:6,fontSize:11,color:"#444",display:"flex",gap:12,flexWrap:"wrap"}}>
        {Object.entries(ZONE_COLOR).map(([z,c]) => (
          <span key={z} style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:10,height:10,borderRadius:"50%",background:c,display:"inline-block"}}/> {z}
          </span>
        ))}
      </div>
    </div>
  );
}
